/* ---- SCR-004: User info page (UUID + user profile, display-only) ----
 *
 * 旧称「設定」/「UUID設定」 → 「ユーザ」/「ユーザ情報」 (Phase 4)。
 * 位置欺瞞検知ステータスは現在地タブの LocationInfoPanel アコーディオンに集約済み。
 */

import { sdkBridge } from '../bridge/sdk-bridge';
import { renderUserInfoForm } from '../components/UserInfoForm';
import { renderUuidCard } from '../components/UuidCard';
import { authService } from '../services/auth.service';

export class SettingsPage {
  private el: HTMLElement;
  private onLogout: (() => void) | null = null;
  /** iOS で window.__staDeviceUuid 注入が遅延するためのポーリングタイマー */
  private uuidPollTimerId: number | null = null;

  constructor(onLogout?: () => void) {
    this.el = document.createElement('div');
    this.el.className = 'screen';
    this.onLogout = onLogout || null;
  }

  render(): HTMLElement {
    // 既存ポーリングがあればクリア (再 render 対策)
    this.clearUuidPolling();
    this.el.innerHTML = '';

    // Header
    const header = document.createElement('div');
    header.className = 'sh';
    header.innerHTML = '<h1>ユーザ情報</h1>';
    this.el.appendChild(header);

    // Scrollable content
    const content = document.createElement('div');
    content.className = 'ss';
    this.el.appendChild(content);

    // UUID card — SDK から取得し、fallback としてサーバ上の値を使用
    const user = authService.getUser();
    const initialUuid = sdkBridge.getUuid();
    const uuid = initialUuid || user?.device_uuid || '取得中...';
    const uuidCard = renderUuidCard(uuid);
    uuidCard.id = 'uuid-card-wrap';
    content.appendChild(uuidCard);

    // iOS では MainViewController の evaluateJavaScript 注入が render 後になることがあるためポーリングで取得を待つ
    if (!initialUuid) {
      this.startUuidPolling(content, user?.device_uuid);
    }

    // User info form (display-only)
    const formContainer = document.createElement('div');
    formContainer.id = 'user-form-container';
    this.renderUserForm(formContainer);
    content.appendChild(formContainer);

    // Logout button
    if (this.onLogout) {
      const logoutBtn = document.createElement('button');
      logoutBtn.className = 'logout-btn';
      logoutBtn.textContent = 'ログアウト';
      logoutBtn.addEventListener('click', () => {
        authService.logout();
        this.onLogout?.();
      });
      content.appendChild(logoutBtn);
    }

    return this.el;
  }

  /**
   * UUID 取得ポーリング (200ms × 25 = 最大 5 秒)。
   * 取得できたら UUID カードを差し替え。タイムアウト時は server の device_uuid を表示、それも無ければ「未取得」。
   */
  private startUuidPolling(container: HTMLElement, fallbackUuid?: string | null): void {
    let attempts = 0;
    const maxAttempts = 25;
    this.uuidPollTimerId = window.setInterval(() => {
      attempts++;
      const uuid = sdkBridge.getUuid();
      if (uuid) {
        this.replaceUuidCard(container, uuid);
        this.clearUuidPolling();
      } else if (attempts >= maxAttempts) {
        this.replaceUuidCard(container, fallbackUuid || '未取得');
        this.clearUuidPolling();
      }
    }, 200);
  }

  private clearUuidPolling(): void {
    if (this.uuidPollTimerId !== null) {
      clearInterval(this.uuidPollTimerId);
      this.uuidPollTimerId = null;
    }
  }

  private replaceUuidCard(container: HTMLElement, uuid: string): void {
    const old = container.querySelector('#uuid-card-wrap');
    if (!old) return;
    const next = renderUuidCard(uuid);
    next.id = 'uuid-card-wrap';
    old.replaceWith(next);
  }

  private async renderUserForm(container: HTMLElement): Promise<void> {
    // まずキャッシュ済みの user を表示。token はあるが currentUser が null
    // (ページリロード直後等) の場合は /users/me から取得して上書き表示する。
    let user = authService.getUser();
    container.innerHTML = '';
    container.appendChild(renderUserInfoForm(user?.name || '', user?.email || ''));

    if (!user) {
      try {
        user = await authService.fetchUser();
        container.innerHTML = '';
        container.appendChild(renderUserInfoForm(user.name || '', user.email || ''));
      } catch (e) {
        console.warn('[SettingsPage] failed to fetch user', e);
      }
    }
  }
}
